import { Icon } from '@iconify/react';
import {
  Alert,
  Badge,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  ThemeProvider,
  Tooltip,
} from '@mui/material';
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { COLOR_THEMES, isColorTheme, type ColorTheme } from '../../api/src/auth/user-preferences';
import { api } from './api/client';
import { Box, Typography } from './components/mui-system';
import { COLOR_THEME_LABELS, createAppTheme } from './theme';

const storageKey = 'seo-dashboard-color-mode';
const accountStorageKey = (userId?: string) => (userId ? `${storageKey}:${userId}` : storageKey);

type Phase = 'loading' | 'ready' | 'saving' | 'load-error';
type ThemeModeContextValue = {
  colorTheme: ColorTheme;
  personal: boolean;
  phase: Phase;
  error: string;
  selectTheme(theme: ColorTheme): Promise<boolean>;
  reload(): void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function initialTheme(userId?: string): ColorTheme {
  const stored = window.localStorage.getItem(accountStorageKey(userId));
  if (isColorTheme(stored)) return stored;
  const legacy = window.localStorage.getItem(storageKey);
  return isColorTheme(legacy) ? legacy : 'dark';
}

export function AppThemeProvider({ children, userId }: { children: ReactNode; userId?: string }) {
  // A session change discards requests and state belonging to the previous account.
  return (
    <ThemeStateProvider key={userId ?? 'guest'} userId={userId}>
      {children}
    </ThemeStateProvider>
  );
}

function ThemeStateProvider({ children, userId }: { children: ReactNode; userId?: string }) {
  const [colorTheme, setColorTheme] = useState<ColorTheme>(() => initialTheme(userId));
  const [phase, setPhase] = useState<Phase>(userId ? 'loading' : 'ready');
  const [personal, setPersonal] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const alive = useRef(false);
  const saving = useRef(false);
  const theme = useMemo(() => createAppTheme(colorTheme), [colorTheme]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    setPhase('loading');
    setError('');
    void api
      .userPreferences()
      .then((preferences) => {
        if (!active) return;
        if (preferences.colorTheme !== undefined) {
          if (!isColorTheme(preferences.colorTheme)) throw new Error('Неизвестная тема в профиле');
          setColorTheme(preferences.colorTheme);
          window.localStorage.setItem(accountStorageKey(userId), preferences.colorTheme);
          setPersonal(true);
        }
        setPhase('ready');
      })
      .catch((caught: unknown) => {
        if (!active) return;
        setError(
          `Не удалось загрузить тему. ${caught instanceof Error ? caught.message : 'Попробуйте ещё раз.'}`,
        );
        setPhase('load-error');
      });
    return () => {
      active = false;
    };
  }, [userId, reloadKey]);

  useEffect(() => {
    document.documentElement.dataset.theme = colorTheme;
    document.documentElement.style.colorScheme = theme.palette.mode;
  }, [colorTheme, theme]);

  async function selectTheme(next: ColorTheme) {
    if (phase !== 'ready' || saving.current) return false;
    setError('');
    if (!userId) {
      window.localStorage.setItem(storageKey, next);
      setColorTheme(next);
      return true;
    }
    saving.current = true;
    setPhase('saving');
    try {
      const saved = await api.saveUserPreferences({ colorTheme: next });
      if (!alive.current) return false;
      if (!isColorTheme(saved.colorTheme)) throw new Error('Сервер не подтвердил выбранную тему');
      window.localStorage.setItem(accountStorageKey(userId), saved.colorTheme);
      setColorTheme(saved.colorTheme);
      setPersonal(true);
      return true;
    } catch (caught) {
      if (alive.current)
        setError(
          `Не удалось сохранить тему. ${caught instanceof Error ? caught.message : 'Попробуйте ещё раз.'}`,
        );
      return false;
    } finally {
      saving.current = false;
      if (alive.current) setPhase('ready');
    }
  }

  return (
    <ThemeModeContext.Provider
      value={{
        colorTheme,
        personal,
        phase,
        error,
        selectTheme,
        reload: () => setReloadKey((key) => key + 1),
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const value = useContext(ThemeModeContext);
  if (!value) throw new Error('useThemeMode must be used inside AppThemeProvider');
  return value;
}

export function ThemeModeButton() {
  const { colorTheme, personal, phase, error, selectTheme, reload } = useThemeMode();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const menuId = useId();
  const title = `Тема интерфейса: ${COLOR_THEME_LABELS[colorTheme]}`;
  const status =
    phase === 'loading'
      ? 'Загружаем личные настройки…'
      : phase === 'saving'
        ? 'Сохраняем…'
        : personal
          ? 'Сохранено в вашем профиле'
          : 'Пока используется выбор этого браузера';
  return (
    <>
      <Tooltip title={error ? 'Тема интерфейса: ошибка настройки' : title}>
        <IconButton
          aria-label={title}
          aria-haspopup="menu"
          aria-expanded={Boolean(anchor)}
          aria-controls={anchor ? menuId : undefined}
          color="inherit"
          size="small"
          onClick={(event) => setAnchor(event.currentTarget)}
        >
          <Badge color="error" variant="dot" invisible={!error}>
            <Icon
              icon={colorTheme === 'light' ? 'solar:sun-2-linear' : 'solar:moon-linear'}
              width={19}
            />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: { sx: { width: 300, maxWidth: 'calc(100vw - 28px)', p: 1 } },
          list: { id: menuId, 'aria-label': 'Тема интерфейса' },
        }}
      >
        <Box component="li" role="presentation" px={1.5} pt={1} pb={1.5}>
          <Typography fontWeight={500}>Тема интерфейса</Typography>
          <Typography fontSize={12} color="text.secondary" aria-live="polite" mt={0.5}>
            {status}
          </Typography>
        </Box>
        {previews.map(({ id, palette }) => (
          <MenuItem
            key={id}
            role="menuitemradio"
            aria-checked={colorTheme === id}
            selected={colorTheme === id}
            disabled={phase !== 'ready'}
            onClick={() =>
              void selectTheme(id).then((saved) => {
                if (saved) setAnchor(null);
              })
            }
            sx={{ borderRadius: 1, gap: 1.5, minHeight: 56 }}
          >
            <Box
              aria-hidden
              sx={{
                width: 44,
                height: 34,
                bgcolor: palette.background.default,
                border: '1px solid',
                borderColor: palette.divider,
                borderRadius: 1,
                p: 0.7,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  bgcolor: palette.background.paper,
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'end',
                  gap: 0.4,
                  p: 0.5,
                }}
              >
                {[palette.success.main, palette.error.main, palette.info.main].map((color) => (
                  <Box key={color} sx={{ flex: 1, height: 3, bgcolor: color, borderRadius: 1 }} />
                ))}
              </Box>
            </Box>
            <Typography flex={1} fontSize={14}>
              {COLOR_THEME_LABELS[id]}
            </Typography>
            {colorTheme === id && <Icon icon="solar:check-read-linear" width={18} />}
          </MenuItem>
        ))}
        {error && (
          <Box component="li" role="presentation">
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          </Box>
        )}
        {phase === 'load-error' && <MenuItem onClick={reload}>Повторить загрузку</MenuItem>}
      </Menu>
    </>
  );
}

const previews = COLOR_THEMES.map((id) => ({ id, palette: createAppTheme(id).palette }));
